<template lang="pug">
.work-orders-main-wrapper
  .work-orders-index
    h2.page-heading Work orders
    //- With Intertia we should adapt the components
    //- to not rely on pure JS logic, and rather use
    //- `Link`s with custom query params and let the BE
    //- handle it the way it wants and re-render.
    .nav.nav-tabs.mb-30
      li.nav-item(
        v-for="page in pageViews"
        :key="page.id"
        :class="{ active: page.isActive }"
      )
        //- Only reload the relevant page props when
        //- the page view changes.
        InertiaLink.nav-link(:href="page.url") {{ page.label }}
    .row.basic-filters
      .col-sm-6.col-md-4.col-lg-3
        .form-group.has-icon-left
          AppInput(
            type="search"
            placeholder="Search work orders"
            :value="work_orders_query.search"
            debounce="0"
            @input="onAppInputSearchChange({ search: $event })"
          )
          i.hi.hi-search
      #wos-table-range-selector.col-sm-6.mt-md-0.d-flex.mt-20
        .range-selector-wrapper.ml-auto.inline-block
          AppDateRangePicker(
            has-clear
            empty-range-text="All time"
            :start-week-on="start_week_on"
            :start-date="startDate"
            :end-date="endDate"
            async
            @change="onDateFilterChange"
            @clear="onFiltersChange({ page: 1, date: null, date_end: null })"
          )
    .row.basic-filters
      .col-sm-6.col-md-4.col-lg-3
        WorkOrdersClientFilter.client-filter(
          :options="groupedClientsForSelect"
          :value="work_orders_query.client_id"
          @input="onFiltersChange({ client_id: $event })"
        )
      .col-sm-6.col-md-4.pull-right.without-label
        .page-header-actions.no-padding-right-sm(v-if="!closedWOsTab")
          AppButton#add-work-order(
            type="primary"
            @click="onShowCreateWorkOrderDialog"
          )
            | Add work order

    AppTableLoading(
      v-if="loading"
      :columns="4"
    )
    WorkOrdersTable.mt-20(
      v-else
      :pagination="pagination"
      :organization="organization"
      :users-avatars="usersAvatars"
      :all-work-orders="allWorkOrders"
      :domain-query="work_orders_query"
      :can-manage-budget="canManageBudget"
      @open-edit-dialog="onShowEditWorkOrderDialog"
      @add-work-order="onShowCreateWorkOrderDialog"
      @page-changed="onFiltersChange({ page: $event })"
    )

  WorkOrdersDialog#new-work-order-wizard(
    v-if="showNewWorkOrderDialog"
    key="create-wo-dialog"
    :can-manage-budget="canManageBudget"
    :grouped-clients="groupedClientsForSelect"
    @close="onCloseCreateWorkOrderDialog"
  )

  WorkOrdersDialog#edit-work-order-wizard(
    v-if="showEditWorkOrderDialog"
    key="edit-wo-dialog"
    editing
    :editing-work-order="editingWorkOrder"
    :default-tab="workOrderDialogTab"
    :can-manage-budget="canManageBudget"
    :grouped-clients="groupedClientsForSelect"
    @close="onCloseEditWorkOrderDialog"
  )

  ScheduleWorkConfirmationDialog(
    v-if="showScheduleWorkConfirmation"
    key="schedule-work-confirmation"
    @close="onCloseScheduleWorkConfirmationDialog"
  )

  JobsDialog#new-job-dialog(
    v-if="showAddJobDialog"
    key="new-job-dialog"
    :organization="organization"
    :time-zone="timeZone"
    :time-zone-abbr="timeZoneAbbr"
    :real-org-time-zone="realOrgTimeZone"
    :work-order="currentWorkOrder"
    :start-week-on="startWeekOn"
    :active-organization-members="activeOrganizationMembers"
    @close="onCloseAddJobDialog"
    @processing="onProcessingJobDialog"
  )

  RecurringJobsDialogManager
</template>

<script>
import { debounce } from 'lodash-es';
// Inertia helpers/utils
import { mapInertiaProps, InertiaFlashWatcher, InertiaLoadingWatcher } from '~/utils/inertia';
// Constants
import { WORK_ORDER_DIALOG_TABS } from '~/app_constants';
// Utils
import { reportError } from '~/utils/errorReporter';
import { cleanWorkOrder } from '~/stores/WorkOrderStore/common/utils';
import { parseInternalDate, formatInternalDate } from '~/utils/time_utils';
import {
  canManageBudget,
  realOrgTimeZone,
  groupedClientsForSelect,
  activeOrganizationMembers,
  addWorkOrdersValidationRules,
} from '~/utils/work_orders';
// Components
import WorkOrdersTable from '~/components/work_orders/WorkOrdersTable.vue';
import JobsDialog from '~/components/work_orders/jobs_dialog/JobsDialog.vue';
import WorkOrdersDialog from '~/components/work_orders/dialog/WorkOrdersDialog.vue';
import WorkOrdersClientFilter from '~/components/work_orders/WorkOrdersClientFilter.vue';
import ScheduleWorkConfirmationDialog from '~/components/work_orders/ScheduleWorkConfirmationDialog.vue';
import RecurringJobsDialogManager from '~/components/work_orders/jobs_dialog/RecurringJobsDialogManager.vue';

// Add VeeValidate rules
addWorkOrdersValidationRules();

// We have to figure out how to deal with "welcome mats" as without
// Inertia, we were just using a simple slim template.
// But not we render the Vue page from the controller.

export default {
  name: 'WorkOrdersIndex',
  components: {
    JobsDialog,
    WorkOrdersTable,
    WorkOrdersDialog,
    WorkOrdersClientFilter,
    RecurringJobsDialogManager,
    ScheduleWorkConfirmationDialog,
  },
  mixins: [InertiaFlashWatcher, InertiaLoadingWatcher],
  // Because this is the page component, the `props` from the controller
  // will be available here as `props` in the Page component.
  props: {
    current_user: Object,
    organization: Object,
    job_site_minimum_radius: Number,
    job_site_default_radius: Number,
    start_week_on: Number,
    organization_members: Array,
    active_org_members: Array,
    beta_dialog: Object,
    clients: Array,
    // FIXME: Use `js-routes` instead
    new_client_url: String,
    edit_client_url: String,
    pagination: Object,
    stats: Object,
    wo_assignees: Array,
    work_orders: Array,
    work_orders_query: Object,
  },
  data() {
    return {
      cancelToken: null,
      currentWorkOrder: null,
      editingWorkOrder: null,
      showAddJobDialog: false,
      processingJobDialog: false,
      showNewWorkOrderDialog: false,
      showEditWorkOrderDialog: false,
      showScheduleWorkConfirmation: false,
      workOrderDialogTab: WORK_ORDER_DIALOG_TABS.general,
    };
  },
  computed: {
    ...mapInertiaProps({
      canManageBudget,
      groupedClientsForSelect,
      activeOrganizationMembers,
      realOrgTimeZone,
      canEditJobs: 'is_owner_manager',
      startWeekOn: 'organization.start_week_on',
      startDate: ({ work_orders_query: query }) => parseInternalDate(query.date),
      endDate: ({ work_orders_query: query }) => parseInternalDate(query.date_end),
      usersAvatars: ({ wo_assignees: woAssignes }) =>
        woAssignes.reduce((avatars, user) => avatars.set(user.id, user.avatar), new Map()),
    }),
    startWeekOn() {
      return this.organization.start_week_on;
    },
    timeZone() {
      return this.organization.time_zone_name;
    },
    timeZoneAbbr() {
      return this.organization.time_zone_abbr;
    },
    organizationMembers() {
      return this.organization_members
        .map(({ id, name }) => ({ id, text: name }))
        .sort((ua, ub) => ua.text.localeCompare(ub.text));
    },
    allWorkOrders() {
      const tz = this.organization.time_zone_name;
      const getters = { organizationMembers: this.organizationMembers };
      const state = {
        clients: this.clients,
        wo_assignees: this.wo_assignees,
      };

      return this.work_orders.map((wo) => cleanWorkOrder(wo, state, getters, tz));
    },
    closedWOsTab() {
      return this.work_orders_query.wo_state === 'closed';
    },
    currentTab() {
      return this.work_orders_query && this.work_orders_query.wo_state;
    },
    currentParams() {
      return Object.entries(this.work_orders_query).filter(([, value]) => !!value);
    },
    pageViews() {
      const statePage = this.work_orders_query.wo_state;
      // FIXME: Change this to use `js-routes` instead of depending on the URL like this.
      const allUrl = this.generateUrlForState('all', this.currentParams);
      const openUrl = this.generateUrlForState('open', this.currentParams);
      const closedUrl = this.generateUrlForState('closed', this.currentParams);

      return [
        {
          id: 'open',
          url: openUrl,
          isActive: statePage === 'open' || !statePage,
          label: `Open (${this.stats.open_count || 0})`,
        },
        {
          id: 'closed',
          url: closedUrl,
          isActive: statePage === 'closed',
          label: `Closed (${this.stats.closed_count || 0})`,
        },
        {
          id: 'all',
          url: allUrl,
          isActive: statePage === 'all',
          label: `All (${this.stats.all_count || 0})`,
        },
      ];
    },
  },
  created() {
    this.debouncedOnFiltersChange = debounce(this.onFiltersChange, 600);
  },
  beforeUnmount() {
    this.debouncedOnFiltersChange.cancel();
  },
  mounted() {
    const url = new URL(this.$page.url, window.location.origin);

    if (url.searchParams.has('createDialog')) {
      this.onShowCreateWorkOrderDialog();
    }

    if (!this.showScheduleWorkConfirmation) {
      this.removeScheduleWorkParamFromHistory();
    }
  },
  methods: {
    generateUrlForState(state, currentParams) {
      const { origin, pathname } = window.location;
      const url = new URL(pathname, origin);
      const searchParams = new URLSearchParams(currentParams);

      if (state === 'open') {
        searchParams.delete('wo_state');
      } else {
        searchParams.set('wo_state', state);
      }
      url.search = searchParams.toString();

      return url.toString();
    },
    onAppInputSearchChange(filtersObj) {
      if (this.cancelToken) {
        this.cancelToken.cancel();
      }
      this.debouncedOnFiltersChange(filtersObj);
    },
    onFiltersChange(filters = {}) {
      const { origin, pathname } = window.location;
      const url = new URL(pathname, origin);
      const search = new URLSearchParams(this.currentParams);

      Object.entries(filters).forEach(([filterKey, filter]) => {
        if (filter) {
          search.set(filterKey, filter);
        } else {
          search.delete(filterKey);
        }
      });
      url.search = search.toString();

      this.$inertia.visit(url.toString(), {
        onCancelToken: (cancelToken) => (this.cancelToken = cancelToken),
      });
    },
    onDateFilterChange(range) {
      this.onFiltersChange({
        page: 1,
        date: range.start ? formatInternalDate(range.start) : null,
        date_end: range.end ? formatInternalDate(range.end) : null,
      });
    },
    onShowEditWorkOrderDialog(workOrder, tab = WORK_ORDER_DIALOG_TABS.general) {
      this.workOrderDialogTab = tab;
      this.editingWorkOrder = workOrder;
      this.showEditWorkOrderDialog = true;
    },
    onShowCreateWorkOrderDialog() {
      this.removeScheduleWorkParamFromHistory();
      this.showNewWorkOrderDialog = true;
    },
    async onCloseCreateWorkOrderDialog() {
      this.showNewWorkOrderDialog = false;

      const url = new URL(this.$page.url, window.location.origin);
      const workOrderId = +url.searchParams.get('schedule_work');

      await this.$nextTick();

      this.onShowScheduleWorkConfirmationDialog(workOrderId);
    },
    onCloseEditWorkOrderDialog() {
      this.showEditWorkOrderDialog = false;
    },
    onShowScheduleWorkConfirmationDialog(workOrderId) {
      if (workOrderId) {
        const workOrder = this.allWorkOrders.find((workOrder) => workOrder.id === workOrderId);

        if (workOrder) {
          this.currentWorkOrder = workOrder;
          this.showScheduleWorkConfirmation = true;
        } else {
          console.error(`Work order with id ${workOrderId} not found`);
          reportError('Work order not found', { workOrderId });
        }
      }
    },
    async onCloseScheduleWorkConfirmationDialog(scheduleWork) {
      this.showScheduleWorkConfirmation = false;

      // Remove the url query param so that we don't open the dialog again
      this.removeScheduleWorkParamFromHistory();

      // We need to await for the next tick, otherwise Vue will not
      // be able to perform the DOM update because it will try to
      // match the removed `ScheduleWorkConfirmation`
      // and insert the `JobsDialog` but will fail.
      await this.$nextTick();

      if (scheduleWork) {
        this.showAddJobDialog = true;
      } else {
        this.currentWorkOrder = null;
      }
    },
    removeScheduleWorkParamFromHistory() {
      const url = new URL(this.$page.url, window.location.origin);

      // First make sure that we have the param `schedule_work` in the URL.
      if (url.searchParams.has('schedule_work')) {
        url.searchParams.delete('schedule_work');
        // this.$page object isn't reactive,
        // and we need to remove schedule_work param here manually
        this.$page.url = url.href;
        window.history.replaceState(null, '', url.toString());
      }
    },
    onProcessingJobDialog() {
      this.processingJobDialog = true;
    },
    onCloseAddJobDialog() {
      this.editingWorkOrder = null;
      this.showAddJobDialog = false;
      this.processingJobDialog = false;
    },
    openEditWorkOrderDialog(workOrder, tab = WORK_ORDER_DIALOG_TABS.general) {
      this.workOrderDialogTab = tab;
      this.editingWorkOrder = workOrder;
      this.setShowEditWorkOrderDialog(true);
    },
    closeEditWorkOrderDialog() {
      this.editingWorkOrder = null;
      this.setShowEditWorkOrderDialog(false);
      this.workOrderDialogTab = WORK_ORDER_DIALOG_TABS.general;
    },
  },
};
</script>
